import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  CheckCircle2, XCircle, RefreshCw, ArrowLeft, Filter,
} from "lucide-react";
import { cn } from "../lib/utils";
import { fmtDateTime } from "../lib/auditFormat";

const LEVEL_STYLES = {
  manager:  { label: "Sales Manager", pill: "bg-blue-100 text-blue-800" },
  pdi_team: { label: "PDI Team",      pill: "bg-amber-100 text-amber-800" },
  admin:    { label: "Admin",         pill: "bg-rose-100 text-rose-800" },
};

export default function SlaEscalationLog() {
  const { activeUser } = useApp();
  const [rows, setRows] = useState(null);
  const [level, setLevel] = useState("all");
  const [ok, setOk] = useState("all");
  const [scanning, setScanning] = useState(false);
  const [scanSummary, setScanSummary] = useState(null);

  const isAdmin = activeUser?.role === "Admin";

  const load = async () => {
    const params = {};
    if (level !== "all") params.level = level;
    if (ok !== "all") params.ok = ok === "ok";
    const data = await api.get("/admin/sla_escalation_log", { params }).then((r) => r.data);
    setRows(data);
  };

  useEffect(() => {
    if (!isAdmin) return;
    load();
    // load()'s dependencies are the query filters — safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, level, ok]);

  const runManualScan = async () => {
    setScanning(true);
    try {
      const res = await api.post("/admin/scan_sla_escalations").then((r) => r.data);
      setScanSummary(res.summary);
      await load();
    } finally { setScanning(false); }
  };

  if (!activeUser) return null;
  if (!isAdmin) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center text-sm text-amber-800">
        SLA Escalation Log is available to Admin users only.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="sla-escalation-log-page">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-primary">
            <ArrowLeft className="h-3 w-3" /> Back to Admin
          </Link>
          <div className="mt-1 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
            Notifications
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-slate-900">
            SLA Escalation Log
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Every WhatsApp alert fired by the SLA escalation matrix. Most recent 200 dispatches.
          </p>
        </div>
        <Button onClick={runManualScan} disabled={scanning} className="gap-2"
          data-testid="sla-manual-scan-btn">
          <RefreshCw className={cn("h-4 w-4", scanning && "animate-spin")} />
          {scanning ? "Scanning…" : "Run Scan Now"}
        </Button>
      </div>

      {scanSummary && (
        <Card className="border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-900"
          data-testid="scan-summary">
          Scan complete — {scanSummary.scanned} tasks scanned, {scanSummary.breached} breached,
          {" "}<b>{scanSummary.sent}</b> new alerts fired
          {scanSummary.failures > 0 && <>, {scanSummary.failures} failures</>}.
        </Card>
      )}

      <Card className="border-slate-200 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-slate-400" />
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="h-9 w-[180px] text-xs" data-testid="sla-log-level">
              <SelectValue placeholder="Level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              <SelectItem value="manager">Sales Manager</SelectItem>
              <SelectItem value="pdi_team">PDI Team</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
          <Select value={ok} onValueChange={setOk}>
            <SelectTrigger className="h-9 w-[150px] text-xs" data-testid="sla-log-ok">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="ok">Delivered</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        {rows === null ? (
          <div className="px-6 py-12 text-center text-sm text-slate-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-slate-400">
            No SLA escalations yet. The scanner runs every 10 minutes — or trigger a manual scan.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="px-3 py-2">Sent At</th>
                  <th className="px-3 py-2">SR / Sale</th>
                  <th className="px-3 py-2">Task</th>
                  <th className="px-3 py-2">Location</th>
                  <th className="px-3 py-2">Level</th>
                  <th className="px-3 py-2">Recipient</th>
                  <th className="px-3 py-2">Phone</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {rows.map((r, i) => {
                  const lvl = LEVEL_STYLES[r.level] || {
                    label: r.level || "—", pill: "bg-slate-100 text-slate-700",
                  };
                  return (
                    <tr key={`${r.task_id}-${r.level}-${r.user_id}-${i}`}
                      className="hover:bg-slate-50"
                      data-testid={`sla-log-row-${i}`}>
                      <td className="px-3 py-1.5 whitespace-nowrap font-mono-data text-[11px] text-slate-600">
                        {r.sent_at ? fmtDateTime(r.sent_at) : "—"}
                      </td>
                      <td className="px-3 py-1.5">
                        {r.sale_id ? (
                          <Link to={`/sales/${r.sale_id}`} className="font-semibold text-slate-800 hover:text-primary">
                            {r.sr_number || "—"}
                          </Link>
                        ) : "—"}
                        <div className="text-[11px] text-slate-500 truncate max-w-[180px]">
                          {r.customer_name || ""}
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-xs text-slate-700">{r.task_label || "—"}</td>
                      <td className="px-3 py-1.5 text-xs text-slate-700">
                        {r.location_name || "—"}
                        {r.location_code && (
                          <span className="ml-1 font-mono-data text-[10px] text-slate-400">
                            {r.location_code}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide", lvl.pill)}>
                          {lvl.label}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-xs font-semibold text-slate-800">
                        {r.user_name || "—"}
                      </td>
                      <td className="px-3 py-1.5 font-mono-data text-[11px] text-slate-600">
                        {r.phone || "—"}
                      </td>
                      <td className="px-3 py-1.5">
                        {r.ok ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
                            <CheckCircle2 className="h-3 w-3" /> Sent
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-rose-200"
                            title={String(r.detail || "")}>
                            <XCircle className="h-3 w-3" /> {String(r.detail || "Failed").slice(0, 40)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
